import {
  Form,
  FormSubmitHandler,
  Skeleton,
  Text,
  useToasts,
} from '@lounge-fe/ui-kit'
import { useTranslation } from 'react-i18next'
import * as z from 'zod'
import { useCustomer } from '../../../features/account/hooks/useCustomer'
import { useAuth } from '@lounge-fe/auth'
import classNames from 'classnames'
import { OutOfStockFormProps } from './types'
import { useExponea } from '../../../hooks/useExponea'
import { useEffect, useState } from 'react'
import { Toast } from '@lounge-fe/ui-kit/src/patterns/ToastProvider/Toast'
import { useShopify } from '@/hooks/useShopify'
import { useFormContext } from 'react-hook-form'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { AddToWishlist } from '@/features/product/components/AddToWishlist/AddToWishlist'

const schema = z.object({
  email: z.string().email(),
  acceptsMarketing: z.boolean(),
  sku_id: z.string(),
})

type Values = z.infer<typeof schema>

type FormValues = {
  email: string | undefined
  acceptsMarketing: boolean
  sku_id: string | undefined
}

interface FormInnerProps {
  skus: string[]
  productId?: string | number
  showWishlistButton?: boolean
}

const FormInner: React.FC<FormInnerProps> = ({
  skus,
  productId,
  showWishlistButton,
}) => {
  const { getFlag } = useFeatureFlags()
  const { t } = useTranslation()
  const {
    setValue,
    formState: { errors, isValid, isSubmitting },
  } = useFormContext<FormValues>()

  useEffect(() => {
    if (skus.length === 0) return
    setValue('sku_id', skus.length ? skus.join(',') : undefined, {
      shouldValidate: true,
    })
  }, [skus])

  return (
    <>
      <Form.Input className="hidden" type="hidden" name="sku_id" />
      <Form.Input
        placeholder={t('account.email_address')}
        name="email"
        className="my-3"
      />
      <Form.Checkbox
        name="acceptsMarketing"
        className={classNames(
          `items-center justify-start bg-surface-secondary-default rounded-sm px-4 py-3 mb-3`,
          `[&_label]:text-sm  [&_label]:font-regular [&_label]:leading-md`,
          `[&_input]:bg-white`,
        )}
        label={t('product.accept_marketing')}
      />
      {errors.sku_id?.message && (
        <Text variant="body-sm" className="text-critical mb-3">
          {t('product.select_an_option')}
        </Text>
      )}

      <div className="flex flex-row gap-4 items-center">
        <Form.Submit
          size="md"
          className="w-full"
          disabled={
            getFlag('product_choice_chips') ? false : isSubmitting || !isValid
          }
        >
          {t('action.notify_me')}
        </Form.Submit>
        {showWishlistButton && productId !== undefined && (
          <AddToWishlist
            productId={productId}
            className="h-10 w-12 border-[#D1D1D1] aspect-[unset] flex-none [&>svg]:w-4 [&>svg]:h-4"
          />
        )}
      </div>

      <Text
        className="text-[10px] leading-[1rem] text-subtle text-left mt-3"
        dangerouslySetInnerHTML={{
          __html: t('product.privacy_policy_statement'),
        }}
      />
    </>
  )
}

export const OutOfStockForm = ({
  selectedVariantIdAndSkus,
  productId,
  title,
  body,
  successMessage,
  event = 'back_in_stock',
  showWishlistButton,
}: OutOfStockFormProps) => {
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const { shop } = useShopify()

  useEffect(() => {
    setSubmitSuccess(false)
  }, [selectedVariantIdAndSkus])

  const { t } = useTranslation()
  const { track, identify, consent, update } = useExponea()
  const { toast } = useToasts()

  const { status: authStatus } = useAuth()
  const isCustomerQueryEnabled = authStatus === 'authenticated'

  const { data: customer, isLoading: isLoadingCustomer } = useCustomer({
    enabled: isCustomerQueryEnabled,
    retry: false,
  })

  const handleSubmit: FormSubmitHandler<Values> = async ({
    email,
    acceptsMarketing,
  }) => {
    try {
      const formattedEmail = email.toLowerCase().trim()
      await identify(formattedEmail, {
        domain: shop,
      })

      const bisVariantSkus: string[] = selectedVariantIdAndSkus.map(
        ([, sku]) => sku,
      )

      await track(event ?? 'back_in_stock', {
        sku_id: bisVariantSkus,
        email: formattedEmail,
        agree: acceptsMarketing,
      })

      if (acceptsMarketing) {
        await consent('email', true)
        await consent('sms', true)
      }

      setSubmitSuccess(true)
    } catch (error) {
      console.error({ error })
      toast({
        severity: 'critical',
        title: t('general.error'),
        message: t('general.error_unexpected'),
      })
    }
  }

  if (submitSuccess) {
    return (
      <div className="flex flex-row gap-4 items-center mt-3">
        <Toast
          severity="success"
          title={
            successMessage?.title ?? t('product.out_of_stock_success_title')
          }
          message={
            successMessage?.message ?? t('product.out_of_stock_success_message')
          }
        />
        {showWishlistButton && productId !== undefined && (
          <AddToWishlist
            productId={productId}
            className="h-10 w-12 rounded-sm border-[#D1D1D1] aspect-[unset] flex-none [&>svg]:w-4 [&>svg]:h-4"
          />
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col flex-1">
      <Text variant="body-sm" className="font-regular my-2">
        {title ?? t('product.out_of_stock_title')}
      </Text>
      <Text
        variant="body-sm"
        className="text-subtle"
        dangerouslySetInnerHTML={{
          __html: body ?? t('product.out_of_stock_subtitle'),
        }}
      ></Text>
      {isLoadingCustomer && isCustomerQueryEnabled ? (
        <Skeleton className="flex flex-col gap-y-3 my-3">
          <Skeleton.Block className="w-full h-12 rounded-sm" />
          <Skeleton.Block className="w-full h-18 rounded-sm" />
          <Skeleton.Block className="w-full h-12 rounded-lg" />
        </Skeleton>
      ) : (
        <Form
          data-testid="out-of-stock-form"
          validationSchema={schema}
          onSubmit={handleSubmit}
          className="flex flex-col flex-1"
          defaultValues={{
            email: customer?.data.email ?? '',
            acceptsMarketing: false,
            sku_id: undefined,
          }}
        >
          <FormInner
            showWishlistButton={showWishlistButton}
            skus={selectedVariantIdAndSkus.map(([, sku]) => sku)}
            productId={productId}
          />
        </Form>
      )}
    </div>
  )
}
